/**
 * authFetch.js - Cross-domain authentication utility
 * For Mid Florida Agencies CRM with API Token support and CSRF refresh
 */

// Production mode check - disable verbose logging in production
const IS_DEV = process.env.NODE_ENV === 'development';

// Safe logging utility - only logs in development, never logs sensitive data
const devLog = (message) => {
    if (IS_DEV) {
        console.log(`[authFetch] ${message}`);
    }
};

// Store CSRF token in memory if cookies fail
let storedCsrfToken = null;
// Store API token for cross-domain authentication
let storedApiToken = null;
// Track if we're currently refreshing CSRF to prevent multiple simultaneous refreshes
let csrfRefreshPromise = null;

/**
 * Store API token from OAuth login
 */
export function setApiToken(token) {
  storedApiToken = token;
  try {
    localStorage.setItem('apiToken', token);
    devLog('API token stored');
  } catch (e) {
    devLog('Could not store API token in localStorage');
  }
}

/**
 * Get stored API token
 */
function getApiToken() {
  if (storedApiToken) {
    return storedApiToken;
  }

  try {
    const token = localStorage.getItem('apiToken');
    if (token) {
      storedApiToken = token;
      return token;
    }
  } catch (e) {
    devLog('Could not read API token from localStorage');
  }

  return null;
}

// Function to get CSRF token from cookies
function getCSRFToken() {
  // First check if we have a stored token (most reliable)
  if (storedCsrfToken) {
    devLog('Using stored CSRF token');
    return storedCsrfToken;
  }

  // Try to get it from cookies - check both possible names
  const cookieNames = ['csrftoken', 'csrfmiddlewaretoken'];
  let cookieValue = null;

  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let cookieName of cookieNames) {
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, cookieName.length + 1) === (cookieName + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(cookieName.length + 1));
          devLog('Found CSRF token in cookie');
          // Store it for next time
          storedCsrfToken = cookieValue;
          break;
        }
      }
      if (cookieValue) break;
    }
  }

  devLog(cookieValue ? 'CSRF token available' : 'No CSRF token found');

  return cookieValue;
}

/**
 * Function to fetch and store a CSRF token
 * Prevents multiple simultaneous refreshes
 */
export async function fetchCsrfToken() {
  // If already refreshing, wait for that to complete
  if (csrfRefreshPromise) {
    devLog('CSRF refresh already in progress, waiting');
    return csrfRefreshPromise;
  }

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Create the refresh promise
  csrfRefreshPromise = (async () => {
    try {
      devLog('Fetching CSRF token');

      const response = await fetch(`${BASE_URL}/api/auth/csrf/`, {
        credentials: 'include',
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();

        // Store the token in multiple places for reliability and validate it
        if (data.csrfToken && data.csrfToken.length > 10) {
          storedCsrfToken = data.csrfToken;

          // Also try to store in localStorage as backup
          try {
            localStorage.setItem('csrfToken', data.csrfToken);
          } catch (e) {
            devLog('Could not store CSRF token in localStorage');
          }

          devLog('CSRF token stored successfully');
          return data.csrfToken;
        } else {
          devLog('Invalid CSRF token received');
          return null;
        }
      } else {
        devLog('Failed to fetch CSRF token: ' + response.status);
        return null;
      }
    } catch (error) {
      devLog('Error fetching CSRF token');
      return null;
    } finally {
      // Clear the promise so future calls can create a new one
      csrfRefreshPromise = null;
    }
  })();

  return csrfRefreshPromise;
}

/**
 * Clear CSRF token and force refresh
 * This should be called after OAuth login completes
 */
export async function refreshCsrfAfterAuth() {
  devLog('Refreshing CSRF token after authentication');

  // Clear existing tokens
  storedCsrfToken = null;
  try {
    localStorage.removeItem('csrfToken');
  } catch (e) {
    devLog('Could not clear localStorage');
  }

  // Fetch new token
  const newToken = await fetchCsrfToken();

  if (newToken) {
    devLog('CSRF token refreshed successfully');
    return newToken;
  } else {
    devLog('Failed to refresh CSRF token');
    return null;
  }
}

/**
 * Enhanced fetch function that includes authentication details
 * Supports both session and API token authentication with automatic CSRF refresh
 */
export function authFetch(url, options = {}) {
  return authFetchInternal(url, options, false, 0);
}

/**
 * Internal fetch function with retry capability
 * Prefers API token if available to avoid unnecessary 403 errors
 */
function authFetchInternal(url, options = {}, useApiToken = false, retryCount = 0) {
  // Maximum retry attempts to prevent infinite loops
  const MAX_RETRIES = 2;

  // More reliable token retrieval with fallbacks
  let csrfToken = null;
  let apiToken = null;

  // Check if we have an API token and prefer it over session auth
  apiToken = getApiToken();

  // If we have an API token and haven't been explicitly told not to use it, use it
  if (apiToken && !useApiToken) {
    useApiToken = true;
    devLog('Using API token authentication');
  }

  // Always get CSRF token for unsafe methods, even when using API token
  // This is because Django's SessionAuthentication may still require CSRF
  // Try stored CSRF token first
  if (storedCsrfToken) {
    csrfToken = storedCsrfToken;
  }
  // Try getting from cookies
  else {
    csrfToken = getCSRFToken();
  }

  // Try localStorage as final fallback
  if (!csrfToken) {
    try {
      csrfToken = localStorage.getItem('csrfToken');
      if (csrfToken) {
        storedCsrfToken = csrfToken; // Update our stored token
      }
    } catch (e) {
      devLog('Could not read from localStorage');
    }
  }

  // Log auth method being used (without exposing tokens)
  devLog(useApiToken ? 'Auth: API token' : (csrfToken ? 'Auth: Session with CSRF' : 'Auth: Session without CSRF'));

  // Start with these defaults
  const defaultOptions = {
    credentials: 'include', // Critical for cross-domain cookies
    headers: {},
  };

  // Determine the request method (default to GET if not specified)
  const method = (options.method || 'GET').toUpperCase();
  const isUnsafeMethod = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);

  // Add authentication headers based on method
  if (useApiToken && apiToken) {
    // Use API token authentication
    defaultOptions.headers['Authorization'] = `Token ${apiToken}`;

    // Also add CSRF token for unsafe methods when using API token
    // Django may require CSRF even with token auth if SessionAuthentication is enabled
    if (isUnsafeMethod && csrfToken && csrfToken.length > 10) {
      defaultOptions.headers['X-CSRFToken'] = csrfToken;
    }
  } else if (csrfToken && csrfToken.length > 10 && isUnsafeMethod) {
    // Only add CSRF token for unsafe methods (POST, PUT, DELETE, PATCH)
    // GET requests should NOT have CSRF tokens as they can cause 403 errors
    defaultOptions.headers['X-CSRFToken'] = csrfToken;
  } else if (!useApiToken && isUnsafeMethod && (!csrfToken || csrfToken.length <= 10)) {
    devLog('Warning: No valid CSRF token for ' + method + ' request');
  }

  // Handle FormData properly by not setting Content-Type
  if (!(options.body instanceof FormData)) {
    defaultOptions.headers['Content-Type'] = 'application/json';
  }

  // Merge our defaults with user options, ensuring headers combine properly
  const finalOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...(options.headers || {}),
    }
  };

  devLog('Request: ' + method + ' ' + url.split('?')[0]);
  
  return fetch(url, finalOptions)
    .then(async response => {
      devLog('Response: ' + response.status);

      // Enhanced 403 handling with CSRF refresh
      if (response.status === 403) {
        devLog('403 Forbidden - checking for CSRF issue');

        // Check if this is a CSRF issue (check response for CSRF error message)
        const responseText = await response.text();
        const isCsrfError = responseText.toLowerCase().includes('csrf');

        // For POST/PUT/DELETE requests with CSRF errors, refresh token and retry
        if (isCsrfError && !useApiToken && retryCount < MAX_RETRIES) {
          devLog('CSRF error detected, refreshing token and retrying');

          // Clear the stored token
          storedCsrfToken = null;
          try {
            localStorage.removeItem('csrfToken');
          } catch (e) {
            // Silently fail
          }

          // Fetch a new CSRF token
          const newToken = await fetchCsrfToken();

          if (newToken) {
            devLog('Retrying with new CSRF token');
            // Retry the request with the new token
            return authFetchInternal(url, options, useApiToken, retryCount + 1);
          }
        }

        // If session auth failed and we have an API token, try with token auth (only once)
        if (!useApiToken && getApiToken() && retryCount < MAX_RETRIES) {
          devLog('Session auth failed, retrying with API token');
          return authFetchInternal(url, options, true, retryCount + 1);
        }

        // If all retries failed, return the original 403 response
        // Re-parse the response since we already read the text
        return new Response(responseText, {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers
        });
      }

      if (response.status === 401) {
        devLog('401 Unauthorized - Session expired');

        // Try API token auth if session auth failed (only once)
        if (!useApiToken && getApiToken() && retryCount < MAX_RETRIES) {
          devLog('Retrying with API token');
          return authFetchInternal(url, options, true, retryCount + 1);
        }

        // Only redirect for non-login/auth URLs to prevent loops
        if (!url.includes('/login') && !url.includes('/auth/')) {
          window.location.href = "/login";
        }
      }

      return response;
    })
    .catch(error => {
      devLog('Network error occurred');
      throw error;
    });
}

/**
 * Logout function
 * @param {string} redirectPath - Optional custom redirect path (e.g., '/login?error=session_timeout')
 * @param {boolean} skipRedirect - If true, skip the automatic redirect (caller handles it)
 */
export const logout = async (redirectPath = '/login', skipRedirect = false) => {
  try {
    const response = await authFetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/logout/`, {
      method: 'POST',
    });

    devLog(response.ok ? 'Logout successful' : 'Logout request completed');
  } catch (error) {
    devLog('Logout error occurred');
  } finally {
    // Clear stored tokens
    storedCsrfToken = null;
    storedApiToken = null;
    try {
      localStorage.removeItem('csrfToken');
      localStorage.removeItem('apiToken');
      localStorage.removeItem('userDetails');
    } catch (e) {
      // Silently fail
    }

    if (!skipRedirect) {
      // Use hash-based URL for HashRouter compatibility
      window.location.href = `/#${redirectPath}`;
    }
  }
};
